"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function ChannelsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => {
      el?.classList?.add('reveal-hidden');
      observer?.observe(el);
    });
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #F5EFE6 0%, #FDFAF6 100%)' }}>
      <div className="max-w-container mx-auto">
        <div className="text-center mb-16 reveal">
          <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-2">
            DUAL CHANNELS · 雙渠道
          </p>
          <h2 className="font-heading font-bold text-brand-fg mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            線上線下，全渠道覆蓋
          </h2>
          <p className="text-brand-muted max-w-xl mx-auto leading-relaxed">
            信悅擁有成熟的線上電商運營體系與廣泛的線下批發網絡，為合作夥伴提供靈活多元的合作模式。
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Online */}
          <div className="reveal rounded-3xl overflow-hidden border border-brand-border bg-white shadow-card group">
            <div className="relative h-56 overflow-hidden">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1b7ae1698-1773033390233.png"
                alt="E-commerce online shopping platform with products displayed on laptop screen"
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700" />
              
              <div className="absolute inset-0 bg-gradient-to-br from-primary/60 to-accent/40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <p className="text-5xl mb-2">🛒</p>
                  <h3 className="font-heading font-bold text-2xl">線上電商</h3>
                  <p className="text-white/80 text-sm">Online E-Commerce</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="space-y-3 mb-6">
                {[
                '入駐淘寶、天貓、京東、拼多多等主流平台',
                '跨境電商出口，覆蓋東南亞、歐美市場',
                '為電商賣家提供代發貨、一件代發服務',
                '完善的電商供應鏈管理體系']?.
                map((item) =>
                <div key={item} className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" variant="solid" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-brand-muted leading-relaxed">{item}</p>
                  </div>
                )}
              </div>
              <Link
                href="/business-scope"
                className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all">
                
                了解電商合作 <span>→</span>
              </Link>
            </div>
          </div>

          {/* Offline */}
          <div className="reveal rounded-3xl overflow-hidden border border-brand-border bg-white shadow-card group" style={{ transitionDelay: '120ms' }}>
            <div className="relative h-56 overflow-hidden">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_15fbf1259-1773033390245.png"
                alt="Warehouse with organized product shelves and wholesale distribution center"
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700" />
              
              <div className="absolute inset-0 bg-gradient-to-br from-accent/60 to-primary/40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <p className="text-5xl mb-2">🏭</p>
                  <h3 className="font-heading font-bold text-2xl">線下批發</h3>
                  <p className="text-white/80 text-sm">Offline Wholesale</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="space-y-3 mb-6">
                {[
                '面向全國批發商、零售商、商超採購',
                '上海工廠直供，省去中間環節降低成本',
                '支持定制OEM/ODM，最低起訂量靈活',
                '專屬客戶經理，快速響應採購需求']?.
                map((item) =>
                <div key={item} className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" variant="solid" size={18} className="text-accent mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-brand-muted leading-relaxed">{item}</p>
                  </div>
                )}
              </div>
              <Link
                href="/registration-center"
                className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all">
                
                申請批發合作 <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>);

}